import { jwtDecode } from 'jwt-decode';
import { DEPRECATE_ROLES, MANAGE_RELEASE_ROLES, normalizeRole } from '../../config/roles';

/**
 * Exact, case-insensitive membership test of the user's `custom:groups` against
 * a permitted-role list. A single provider prefix is stripped
 * (e.g. "cognito:DLI_Admin_dev" → "dli_admin_dev"), but the match is exact
 * afterwards so names like "non-admin" or "DLI_Admin_dev_viewer" do not match.
 */
const hasAnyRole = (groups, permitted) => {
  if (!Array.isArray(groups)) return false;
  return groups.map(normalizeRole).some((role) => permitted.includes(role));
};

export const parseGroups = (rawGroups) => {
  if (!rawGroups) return [];
  try {
    if (typeof rawGroups === 'string') {
      return JSON.parse(rawGroups.replace(/\\/g, ''));
    }
    if (Array.isArray(rawGroups)) return rawGroups;
    if (typeof rawGroups === 'object') return Object.values(rawGroups);
  } catch (e) {
    console.warn('Failed to parse custom:groups, fallback to raw value:', e);
    return [rawGroups];
  }
  return [];
};

/**
 * Whether any of the user's groups grants deprecate permission.
 *
 * Permitted roles come from env-driven config (`src/config/roles.js`), not
 * hardcoded here.
 */
export const canDeprecate = (groups = []) => hasAnyRole(groups, DEPRECATE_ROLES);

/**
 * Whether any of the user's groups grants access to Manage Release.
 *
 * Only the admin and modelling Cognito groups for the current environment
 * (e.g. `DLI_Admin_dev` / `DLI_Modelling_dev`) qualify; the group list comes
 * from the `custom:groups` claim on the id token.
 */
export const canManageRelease = (groups = []) => hasAnyRole(groups, MANAGE_RELEASE_ROLES);

//decode userInfo from token
export const decodeUserInfo = (idToken, refreshToken) => {
  try {
    const decoded = jwtDecode(idToken);
    const givenName = decoded?.given_name || '';
    const familyName = decoded?.family_name || '';
    return {
      email: decoded.email || '',
      userId: decoded?.identities?.[0]?.userId || '',
      refreshToken,
      initials: `${givenName[0] || ''}${familyName[0] || ''}`,
      fullName: `${givenName} ${familyName}`.trim(),
      groups: parseGroups(decoded['custom:groups']),
    };
  } catch (error) {
    console.error('Token decode error:', error);
    return null;
  }
};
